import { useState, type ReactElement } from "react";
import type { CreateSubmissionRequest } from "../hooks/useSubmissionMutations";
import { useTMDBDetails, useTMDBSearch } from "../hooks/useTMDB";
import { TMDBSearchForm } from "./TMDBSearchForm";
import { TMDBSearchResults } from "./TMDBSearchResults";

interface TMDBSearchWidgetProps {
  onAutofill: (data: Partial<CreateSubmissionRequest>) => void;
  currentYear?: number;
}

/**
 * TMDB Search Widget
 * Search for movies on TMDB and auto-populate metadata
 */
export function TMDBSearchWidget({ onAutofill, currentYear }: Readonly<TMDBSearchWidgetProps>): ReactElement {
  const [query, setQuery] = useState("");
  const [year, setYear] = useState(currentYear?.toString() || "");
  const [loadingId, setLoadingId] = useState<number | null>(null);

  const { results, loading: searchLoading, search, clear } = useTMDBSearch();
  const { fetch: fetchDetails } = useTMDBDetails();

  const handleSearch = () => {
    if (query.trim()) {
      void search(query, year ? Number.parseInt(year, 10) : undefined);
    }
  };

  const handleSelectMovie = async (tmdbId: number) => {
    setLoadingId(tmdbId);

    try {
      // Fetch full movie details
      const movieDetails = await fetchDetails(tmdbId);

      if (!movieDetails) {
        setLoadingId(null);
        return;
      }

      // Extract and format data for autofill
      const director = movieDetails.credits?.crew.find((c) => c.job === "Director");
      const cast = movieDetails.credits?.cast?.slice(0, 10).map((c) => c.name) || [];
      const genres = movieDetails.genres?.map((g) => g.name) || [];

      // Extract key crew members (excluding Director as it's stored separately)
      const importantCrewRoles = new Set([
        "Screenplay",
        "Writer",
        "Story",
        "Director of Photography",
        "Producer",
        "Executive Producer",
        "Original Music Composer",
        "Editor",
        "Production Design",
      ]);
      const crew =
        movieDetails.credits?.crew
          ?.filter((c) => c.job !== "Director" && importantCrewRoles.has(c.job))
          .map((c) => ({ name: c.name, job: c.job, department: c.department })) || [];

      // Extract language (ISO 639-1 code, e.g., "en", "es", "fr")
      const language = movieDetails.original_language || undefined;

      // Extract primary production country (ISO 3166-1 code, e.g., "US", "GB", "FR")
      const country = movieDetails.production_countries?.[0]?.iso_3166_1 || undefined;

      const posterUrl = movieDetails.poster_path
        ? `https://image.tmdb.org/t/p/w500${movieDetails.poster_path}`
        : undefined;
      const backdropUrl = movieDetails.backdrop_path
        ? `https://image.tmdb.org/t/p/original${movieDetails.backdrop_path}`
        : undefined;

      const autofillData = {
        title: movieDetails.title,
        releaseYear: movieDetails.release_date
          ? Number.parseInt(movieDetails.release_date.split("-")[0], 10)
          : undefined,
        runtime: movieDetails.runtime || undefined,
        synopsis: movieDetails.overview || undefined,
        description: movieDetails.overview || undefined,
        language,
        country,
        director: director?.name || undefined,
        cast,
        crew,
        genres,
        tmdbId: movieDetails.id,
        posterUrl,
        backdropUrl,
      };

      // Autofill the form
      onAutofill(autofillData);

      // Clear search after successful autofill
      clear();
      setQuery("");
      setYear("");
      setLoadingId(null);
    } catch (error) {
      console.error("Failed to fetch movie details:", error);
      setLoadingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <TMDBSearchForm
        query={query}
        year={year}
        loading={searchLoading}
        onQueryChange={setQuery}
        onYearChange={setYear}
        onSearch={handleSearch}
      />

      {results.length > 0 && <TMDBSearchResults results={results} loadingId={loadingId} onSelect={handleSelectMovie} />}
    </div>
  );
}
